import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Paper, List, ListItemButton, ListItemText, Typography, Button, TextField,
  IconButton, CircularProgress, Alert, Divider, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import StableCKEditor from '../StableCKEditor';
import ClassicEditor from '../../utils/ckeditor';
import { renderRichHtml, toEditorHtml } from '../../utils/sanitizeHtml';
import { projectPagesAPI } from '../../services/api';

const formatSize = (b) => {
  if (!b) return '0 B';
  const u = ['B', 'KB', 'MB']; let i = 0; let n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
};

/**
 * Per-project mini-CMS ("Dokumente"): sidebar page list + rich-text editor +
 * document uploads. Internal only (backend blocks clients).
 */
function ProjectDocs({ projectId }) {
  const [pages, setPages] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [page, setPage] = useState(null);          // full active page (with content)
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [editing, setEditing] = useState(false);
  const justCreatedRef = useRef(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const loadList = useCallback(async (selectId) => {
    setLoadingList(true);
    try {
      const res = await projectPagesAPI.list(projectId);
      setPages(res.data || []);
      if (selectId) setActiveId(selectId);
      else if (!activeId && res.data && res.data.length) setActiveId(res.data[0].id);
    } catch (err) {
      setError('Seiten konnten nicht geladen werden');
    } finally {
      setLoadingList(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => { loadList(); }, [loadList]);

  const loadPage = useCallback(async (id) => {
    if (!id) { setPage(null); return; }
    setLoadingPage(true);
    try {
      const [p, a] = await Promise.all([
        projectPagesAPI.get(id),
        projectPagesAPI.listAttachments(id),
      ]);
      setPage(p.data);
      setTitle(p.data.title);
      setContent(p.data.content || '');
      setAttachments(a.data || []);
      // open in edit mode only for a freshly created page; otherwise read mode
      setEditing(justCreatedRef.current === id);
      justCreatedRef.current = null;
    } catch (err) {
      setError('Seite konnte nicht geladen werden');
    } finally {
      setLoadingPage(false);
    }
  }, []);

  useEffect(() => { loadPage(activeId); }, [activeId, loadPage]);

  const createPage = async () => {
    try {
      const res = await projectPagesAPI.create(projectId, { title: 'Neue Seite', content: '' });
      justCreatedRef.current = res.data.id;
      await loadList(res.data.id);
    } catch (err) {
      setError('Seite konnte nicht angelegt werden');
    }
  };

  const save = async () => {
    if (!page) return;
    if (!title.trim()) { setError('Titel erforderlich'); return; }
    setSaving(true);
    setError(null);
    try {
      await projectPagesAPI.update(page.id, { title: title.trim(), content });
      setEditing(false);
      await loadList(page.id);
    } catch (err) {
      setError('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!page) return;
    if (!window.confirm(`Seite „${page.title}" wirklich löschen?`)) return;
    try {
      await projectPagesAPI.delete(page.id);
      setActiveId(null);
      setPage(null);
      await loadList();
    } catch (err) {
      setError('Löschen fehlgeschlagen');
    }
  };

  const uploadFiles = async (files) => {
    if (!page) return;
    setUploading(true);
    setError(null);
    try {
      for (const f of Array.from(files)) {
        try { await projectPagesAPI.uploadAttachment(page.id, f); }
        catch (err) { setError(err.response?.data?.error || `Upload fehlgeschlagen: ${f.name}`); }
      }
      const a = await projectPagesAPI.listAttachments(page.id);
      setAttachments(a.data || []);
    } finally {
      setUploading(false);
    }
  };

  const download = async (att) => {
    try {
      const res = await projectPagesAPI.downloadAttachment(att.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: att.mime_type || 'application/octet-stream' }));
      const a = document.createElement('a');
      a.href = url; a.download = att.original_filename; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { setError('Download fehlgeschlagen'); }
  };

  const deleteAtt = async (att) => {
    try {
      await projectPagesAPI.deleteAttachment(att.id);
      setAttachments((prev) => prev.filter((x) => x.id !== att.id));
    } catch (err) { setError('Datei konnte nicht gelöscht werden'); }
  };

  return (
    <Box sx={{ display: 'flex', gap: 2, minHeight: '60vh' }}>
      {/* Sidebar: page list */}
      <Paper variant="outlined" sx={{ width: 280, flexShrink: 0, borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight={600}>Seiten</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={createPage}>Neu</Button>
        </Box>
        <Divider />
        {loadingList ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={22} /></Box>
        ) : pages.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            Noch keine Seiten. Lege die erste an.
          </Typography>
        ) : (
          <List dense sx={{ overflowY: 'auto' }}>
            {pages.map((p) => (
              <ListItemButton key={p.id} selected={p.id === activeId} onClick={() => setActiveId(p.id)}>
                <DescriptionIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                <ListItemText
                  primary={p.title}
                  secondary={p.attachment_count ? `${p.attachment_count} Datei(en)` : null}
                  primaryTypographyProps={{ noWrap: true }}
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </Paper>

      {/* Main: editor + attachments */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {!activeId ? (
          <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
            <DescriptionIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              Wähle links eine Seite oder lege eine neue an.
            </Typography>
          </Paper>
        ) : loadingPage ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : page ? (
          <Box>
            {editing ? (
              <>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
                  <TextField
                    variant="standard" fullWidth value={title} autoFocus
                    onChange={(e) => setTitle(e.target.value)}
                    InputProps={{ sx: { fontSize: '1.4rem', fontWeight: 600 } }}
                  />
                  <Button variant="contained" startIcon={<SaveIcon />} onClick={save}
                    disabled={saving}
                    sx={{ whiteSpace: 'nowrap', bgcolor: 'primary.main', color: '#fff',
                          '&:hover': { bgcolor: 'primary.dark' } }}>
                    {saving ? 'Speichern…' : 'Speichern'}
                  </Button>
                  <Button onClick={() => { setEditing(false); loadPage(page.id); }} disabled={saving}>
                    Abbrechen
                  </Button>
                </Box>
                <Box sx={{
                  border: '1px solid rgba(0,0,0,0.23)', borderRadius: 1, mb: 1,
                  '& .ck-editor__editable': { minHeight: '320px' },
                }}>
                  <StableCKEditor
                    key={page.id}
                    editor={ClassicEditor}
                    data={toEditorHtml(content)}
                    config={{
                      placeholder: 'Inhalt der Seite…',
                      toolbar: ['heading', '|', 'bold', 'italic', 'link', '|',
                        'bulletedList', 'numberedList', '|', 'blockQuote', 'insertTable', '|', 'undo', 'redo'],
                    }}
                    onChange={(_, editor) => setContent(editor.getData())}
                  />
                </Box>
              </>
            ) : (
              <>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5 }}>
                  <Typography variant="h5" fontWeight={700} sx={{ flex: 1 }}>{title}</Typography>
                  <Tooltip title="Bearbeiten">
                    <IconButton color="primary" onClick={() => setEditing(true)}><EditIcon /></IconButton>
                  </Tooltip>
                  <Tooltip title="Seite löschen">
                    <IconButton color="error" onClick={remove}><DeleteIcon /></IconButton>
                  </Tooltip>
                </Box>
                {content && content.trim() ? (
                  <Box
                    sx={{
                      '& p': { my: 1, lineHeight: 1.6 },
                      '& h1,& h2,& h3': { mt: 2, mb: 1, fontWeight: 700 },
                      '& ul,& ol': { pl: 3, my: 1 },
                      '& li': { mb: 0.5 },
                      '& a': { color: 'primary.main' },
                      '& table': { borderCollapse: 'collapse', my: 1 },
                      '& td,& th': { border: '1px solid #ddd', p: 1 },
                      '& blockquote': { borderLeft: '3px solid #ccc', pl: 2, color: 'text.secondary', my: 1 },
                    }}
                    dangerouslySetInnerHTML={{ __html: renderRichHtml(content) }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Noch kein Inhalt — über das Stift-Icon bearbeiten.
                  </Typography>
                )}
                {page.editor_name && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                    Zuletzt bearbeitet von {page.editor_name}
                  </Typography>
                )}
              </>
            )}

            {/* Attachments */}
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AttachFileIcon color="primary" fontSize="small" />
                <Typography variant="subtitle1" fontWeight={600}>
                  Dokumente{attachments.length ? ` (${attachments.length})` : ''}
                </Typography>
              </Box>
              <Box
                onClick={() => fileRef.current && fileRef.current.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files.length) uploadFiles(e.dataTransfer.files); }}
                sx={{
                  border: '2px dashed', borderColor: 'divider', borderRadius: 2, p: 2,
                  textAlign: 'center', cursor: 'pointer', mb: 1,
                }}
              >
                <input ref={fileRef} type="file" multiple hidden
                  onChange={(e) => { if (e.target.files.length) uploadFiles(e.target.files); e.target.value = ''; }} />
                <CloudUploadIcon sx={{ color: 'text.secondary', mr: 1, verticalAlign: 'middle' }} />
                <Typography variant="body2" component="span" color="text.secondary">
                  Dokument hierher ziehen oder klicken · max. 20 MB
                </Typography>
              </Box>
              {uploading && <CircularProgress size={18} sx={{ mb: 1 }} />}
              {attachments.length > 0 && (
                <List dense>
                  {attachments.map((att) => (
                    <ListItemButton key={att.id} disableRipple sx={{ cursor: 'default' }}>
                      <DescriptionIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <ListItemText
                        primary={att.original_filename}
                        secondary={`${formatSize(att.file_size)} · ${att.uploader_name || ''}`}
                      />
                      <IconButton size="small" onClick={() => download(att)} title="Herunterladen">
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => deleteAtt(att)} title="Löschen">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

export default ProjectDocs;
